import React, { useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import ToggleButton from 'components/v2/compiler/panels/toggleable/ToggleButton';
import WizardTextInputWithAutoComplete from 'components/v2/epd-wizard-inputs/WizardTextInputWithAutoComplete';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import { RouteComponentProps } from 'react-router-dom';
import { CompanySize } from 'services/EpdClient';
import {
  ExpandablePanel,
  ExpandablePanelHeaderName,
  FieldPanelFullWidth,
  H1NewUI,
  HeaderCaptionSemiBold,
} from 'styles/v2/Styles.styled';
import { postSearchCompanies } from 'services/api/adminApi';
import SelectDataField from 'components/v2/form/select-data-field/SelectDataField';
import { useAdminLookup } from 'services/api/queries';
import { AdminLookupKeyEnum, Option, VerifierType } from 'types/types';
import TextWithAnimatedEllipsis from 'components/v2/file-upload/TextWithAnimatedEllipsis';
import WizardCheckboxComponent from 'components/v2/epd-wizard-inputs/WizardCheckbox';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { QUERY_KEYS } from 'services/api/constants';
import AdminSearchCompaniesResultsTable from './AdminSearchCompaniesResultsTable';
import AdminSearchCompaniesQueryHelper from './AdminSearchCompaniesQueryHelper';
import AdminSearchCompaniesRemoveFilterChips from './AdminSearchCompaniesRemoveFilterChips';
import AdminMainContainer from 'components/admin/search/AdminMainContainer';
import AuthService from 'services/AuthService';

const COMPANY_SIZE_OPTIONS: Option[] = Object.values(CompanySize).map((x) => ({ label: x, value: x }));
const VERIFIER_TYPE_OPTIONS: Option[] = Object.values(VerifierType).map((x) => ({ label: x, value: x }));

const AdminSearchCompanies: React.FC<RouteComponentProps> = ({ location, history }) => {
  const currentFilter = AdminSearchCompaniesQueryHelper.parse(location.search);
  const [companyNameAutoCompleteList, setCompanyNameAutoCompleteList] = useState<string[]>([]);
  const countries = useAdminLookup(AdminLookupKeyEnum.COUNTRIES).data;
  const companyRoles = useAdminLookup(AdminLookupKeyEnum.COMPANY_ROLES).data;
  const queryClient = useQueryClient();

  const isLicenseeAdmin = !!AuthService.isLicenseeAdmin();
  const isLoading = !countries || !companyRoles;
  const isSingleCountryForLicenseeAdmin = isLicenseeAdmin && (countries?.length ?? 0) === 1;

  const countriesKey = useMemo(() => {
    return (countries ?? []).map((c) => c.value).join('|');
  }, [countries]);

  const applyLicenseeCountryRestriction = useMemo(() => {
    return (filter: any) => {
      if (!isLicenseeAdmin) return filter;
      if (!countries || countries.length === 0) return filter;

      const countryIdsForLicenseeAdmin = countries.map((c) => c.value);

      if (countries.length === 1) {
        return {
          ...filter,
          countryIds: [countries[0].value],
        };
      }

      const selectedIds = (filter.countryIds ?? []) as Array<Option['value']>;
      if (selectedIds.length === 0) {
        return {
          ...filter,
          countryIds: countryIdsForLicenseeAdmin,
        };
      }

      return {
        ...filter,
        countryIds: selectedIds.filter((id) => countryIdsForLicenseeAdmin.includes(id)),
      };
    };
  }, [isLicenseeAdmin, countries]);

  useEffect(() => {
    if (!isLicenseeAdmin) return;
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.LOOKUP(AdminLookupKeyEnum.COUNTRIES),
    });
  }, []);

  const searchResultsQuery = useQuery({
    queryKey: [QUERY_KEYS.ADMIN_COMPANIES, location.search, countriesKey, isLicenseeAdmin ? 'LA' : 'NLA'],
    queryFn: () => postSearchCompanies(applyLicenseeCountryRestriction(currentFilter)),
    placeholderData: (prev) => prev, // This keeps previous data to avoid major layout shifts when changing page
    retry: false,
    enabled: !isLoading,
  });

  if (isLoading) {
    return (
      <AdminMainContainer>
        <TextWithAnimatedEllipsis>Loading</TextWithAnimatedEllipsis>
      </AdminMainContainer>
    );
  }

  return (
    <AdminMainContainer>
      <H1NewUI>{isLicenseeAdmin ? 'Licensee organisations' : 'Organisations'}</H1NewUI>

      <Formik
        initialValues={currentFilter}
        onSubmit={(values) => {
          const newSearchParams = AdminSearchCompaniesQueryHelper.stringify({
            ...values,
            pageNumber: 1,
          });
          history.push(location.pathname + newSearchParams);
        }}
        enableReinitialize
      >
        {({ values, setFieldValue, dirty, submitForm }) => {
          const selectedCountryOptions = useMemo(() => {
            const ids = (values.countryIds ?? []) as Array<Option['value']>;
            return countries.filter((c) => ids.includes(c.value));
          }, [countries, values.countryIds]);

          const countrySelectValue = isSingleCountryForLicenseeAdmin ? countries[0] ?? null : selectedCountryOptions;
          return (
            <ExpandablePanel
              style={{ marginTop: 38 }}
              headerTemplate={(options) => (
                <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
                  <ExpandablePanelHeaderName>
                    <HeaderCaptionSemiBold>Filters</HeaderCaptionSemiBold>
                    <ToggleButton options={options} />
                  </ExpandablePanelHeaderName>

                  {options.collapsed && (
                    <ChipsList>
                      <AdminSearchCompaniesRemoveFilterChips filter={currentFilter} />
                    </ChipsList>
                  )}
                </div>
              )}
              toggleable
            >
              <Form>
                <TwoColumnsLayout>
                  <WizardTextInputWithAutoComplete
                    name="name"
                    label="Organisation name"
                    value={values.name}
                    triggerOnChangedFor="keypress"
                    onChanged={setFieldValue}
                    onSelectSuggested={(value) => {
                      setFieldValue('name', value);
                      submitForm();
                    }}
                    onSubmit={() => {
                      submitForm();
                    }}
                    placeholder="Type here"
                    completeMethod={async ({ query }) => {
                      let countryIdsForSuggestSearchFilter: string[] = [];

                      if (Array.isArray(countrySelectValue) && countrySelectValue.length > 0) {
                        countryIdsForSuggestSearchFilter = countrySelectValue.map((x) => x.value);
                      } else if (!Array.isArray(countrySelectValue) && countrySelectValue) {
                        countryIdsForSuggestSearchFilter = [countrySelectValue.value];
                      } else if (isLicenseeAdmin) {
                        countryIdsForSuggestSearchFilter = countries.map((c) => c.value);
                      }

                      const result = await postSearchCompanies({
                        ...values,
                        countryIds: countryIdsForSuggestSearchFilter,
                        name: query,
                        pageNumber: 1,
                        pageSize: 5,
                      });
                      setCompanyNameAutoCompleteList(result.items.map((x) => x.name));
                    }}
                    suggestions={companyNameAutoCompleteList}
                  />

                  <SelectDataField
                    name="countryIds"
                    label="Organisation country"
                    options={countries}
                    value={countrySelectValue as any}
                    isMulti={!isSingleCountryForLicenseeAdmin}
                    isClearable={!isSingleCountryForLicenseeAdmin}
                    disabled={isSingleCountryForLicenseeAdmin}
                    onChange={(newValue: Option | Option[] | null) => {
                      const ids = Array.isArray(newValue) ? newValue.map((x) => x.value) : newValue ? [newValue.value] : [];
                      setFieldValue('countryIds', ids);
                    }}
                  />

                  <SelectDataField name="size" label="Organisation size" options={COMPANY_SIZE_OPTIONS} isClearable />

                  <FilterCheckboxContainer>
                    <WizardCheckboxComponent
                      name="includeArchived"
                      inputId="includeArchived"
                      label="Include archived"
                      value={values.includeArchived}
                      onChanged={setFieldValue}
                      size="small"
                    />
                  </FilterCheckboxContainer>

                  <SelectDataField name="roleId" label="Organisation type" options={companyRoles} isClearable />

                  <FilterCheckboxContainer>
                    <WizardCheckboxComponent
                      name="onlyHolding"
                      inputId="onlyHolding"
                      label="Only holding companies"
                      value={values.onlyHolding}
                      onChanged={setFieldValue}
                      size="small"
                    />
                  </FilterCheckboxContainer>

                  <SelectDataField name="verifierType" label="Verifier type" options={VERIFIER_TYPE_OPTIONS} isClearable />

                  <FilterCheckboxContainer>
                    <WizardCheckboxComponent
                      name="onlyProcessCertification"
                      inputId="onlyProcessCertification"
                      label="Only process certification"
                      value={values.onlyProcessCertification}
                      onChanged={setFieldValue}
                      size="small"
                    />
                  </FilterCheckboxContainer>
                </TwoColumnsLayout>

                <ButtonsRow>
                  <SecondaryButton
                    type="reset"
                    label="Clear"
                    onClick={() => history.push(location.pathname)}
                    disabled={!(dirty || AdminSearchCompaniesQueryHelper.isFilterActive(currentFilter))}
                  />
                  <PrimaryButton
                    type="submit"
                    label="Search"
                    loading={!!searchResultsQuery.data && searchResultsQuery.isFetching}
                    disabled={!dirty}
                  />
                </ButtonsRow>
              </Form>
            </ExpandablePanel>
          );
        }}
      </Formik>

      {(() => {
        if (searchResultsQuery.error) {
          const message = searchResultsQuery.error instanceof Error ? searchResultsQuery.error.message : 'Unknown error';
          return <FieldPanelFullWidth>Failed to load results: {message}</FieldPanelFullWidth>;
        }

        const searchResult = searchResultsQuery.data;
        if (!searchResult) {
          return null;
        }

        if (searchResult.totalCount === 0) {
          return <FieldPanelFullWidth>No results found</FieldPanelFullWidth>;
        }

        return (
          <FieldPanelFullWidth>
            <AdminSearchCompaniesResultsTable data={searchResult} showLoading={searchResultsQuery.isFetching} />
          </FieldPanelFullWidth>
        );
      })()}
    </AdminMainContainer>
  );
};

export default withGreyBackground(AdminSearchCompanies);

const ChipsList = styled.div`
  flex-grow: 1;
  display: flex;
  padding-left: 30px;
  flex-wrap: wrap;
  column-gap: 7px;
  row-gap: 4px;
`;

const TwoColumnsLayout = styled.div`
  display: grid;
  column-gap: 30px;
  row-gap: 29px;
  grid-template-columns: 1fr;

  ${(props) => props.theme.media.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FilterCheckboxContainer = styled.div`
  align-self: flex-end;
  background-color: ${(props) => props.theme.colors.primaryOrange01};
  display: flex;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 13px;
  padding-bottom: 15px;
  border-radius: 2px;
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 41px;
  gap: 16px;

  && ${PrimaryButton}, && ${SecondaryButton} {
    min-width: 140px;
  }
`;
